import { useMemo, useRef } from 'react';
import type {
  ReactNode,
  CSSProperties,
  Ref,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';
import {
  Table,
  Stack,
  Text,
  Button,
  Center,
  Loader,
  Card,
  Box,
  ScrollArea,
  LoadingOverlay,
} from '@mantine/core';
import { SortableTableHeader } from '../SortableTableHeader';
import { Pagination } from '../Pagination';
import type { ColumnConfig, SortConfig } from '../../../hooks/useColumnConfig';
import classes from './ConfigurableTable.module.css';

/**
 * Column renderer function type
 */
export type CellRenderer<T> = (item: T, column: ColumnConfig) => ReactNode;

/**
 * Props for ConfigurableTable
 */
export interface ConfigurableTableProps<T> {
  /** Data items to display */
  data: T[];
  /** Column configuration */
  columns: ColumnConfig[];
  /** Get unique key for each row */
  getRowKey: (item: T) => string;
  /** Render cell content for a column */
  renderCell: CellRenderer<T>;
  /** Render actions column (optional) */
  renderActions?: (item: T) => ReactNode;
  /** Current sort configuration */
  sortConfig: SortConfig | null;
  /** Handle sort change */
  onSort: (key: string) => void;
  /** Loading state */
  loading?: boolean;
  /** Current page */
  page: number;
  /** Page size */
  pageSize: number;
  /** Total item count */
  totalCount: number;
  /** Handle page change */
  onPageChange: (page: number) => void;
  /** Handle page size change */
  onPageSizeChange: (size: number) => void;
  /** Number of visible columns (for empty state colspan) */
  visibleColumnCount: number;

  // --- Empty state ---
  /** Simple empty state message (ignored if emptyStateContent is provided) */
  emptyMessage?: string;
  /** Fully custom empty state content (overrides emptyMessage) */
  emptyStateContent?: ReactNode;
  /** Whether filters are active (for showing clear button in default empty state) */
  hasActiveFilters?: boolean;
  /** Clear filters callback (for default empty state) */
  onClearFilters?: () => void;

  // --- Sticky columns ---
  /** Pin the first visible column so it stays fixed during horizontal scroll */
  stickyFirstColumn?: boolean;
  /** Pin the actions column to the right edge */
  stickyActions?: boolean;

  // --- Column widths ---
  /** Return a min-width for a given column id */
  getColumnMinWidth?: (columnId: string) => number | undefined;
  /**
   * MED-1367: opt-in drag-to-resize. Each header gets a handle on its right
   * edge. The caller owns the widths (columnWidths) and receives each change
   * (onColumnResize). Off by default, so the other tables do not change.
   */
  resizableColumns?: boolean;
  /** MED-1367: current width per column id, used when resizableColumns is on */
  columnWidths?: Record<string, number>;
  /** MED-1367: called with the new width while a handle moves */
  onColumnResize?: (columnId: string, width: number) => void;
  /** MED-1367: called on a double-click on a handle, to restore the default width */
  onColumnResetWidth?: (columnId: string) => void;

  // --- Row customization ---
  /** Return inline styles for a row based on the item */
  getRowStyle?: (item: T) => CSSProperties | undefined;
  /** Row click handler */
  onRowClick?: (item: T) => void;

  // --- Leading column (e.g. checkbox) ---
  /** Render a leading column header (e.g. select-all checkbox) */
  renderLeadingHeader?: () => ReactNode;
  /** Render a leading column cell for each row (e.g. row checkbox) */
  renderLeadingCell?: (item: T) => ReactNode;
  /** Leading column width (default 40) */
  leadingColumnWidth?: number;

  // --- Actions column ---
  /** Actions column width (default 50) */
  actionsColumnWidth?: number;

  // --- Pagination ---
  /** Label shown after total count in pagination (e.g. "participants") */
  entityLabel?: string;

  // --- Scroll viewport ---
  /** Ref to the horizontal scroll viewport (e.g. to show a scroll hint) */
  viewportRef?: Ref<HTMLDivElement>;
}

// MED-1367: the narrowest a column can get by drag. Callers can clamp higher.
const MIN_RESIZE_WIDTH = 40;
// MED-1367: one arrow key press on a focused handle moves the edge this far.
const KEYBOARD_RESIZE_STEP = 16;

interface ColumnResizeHandleProps {
  columnId: string;
  /** Accessible name, e.g. "Resize Email column" */
  ariaLabel: string;
  width: number | undefined;
  onResize: (columnId: string, width: number) => void;
  onReset?: (columnId: string) => void;
}

/**
 * MED-1367: drag handle on the right edge of a header cell. Pointer events
 * cover mouse, pen and touch. The handle is also a focusable separator, so
 * the arrow keys resize the column from the keyboard. MED-1308: the Data
 * Library tables and its cohort panel use it too.
 */
export function ColumnResizeHandle({
  columnId,
  ariaLabel,
  width,
  onResize,
  onReset,
}: ColumnResizeHandleProps) {
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // The rendered width of the header cell. It can be wider than the stored
  // width when the table stretches to fill its card.
  const currentWidth = (el: HTMLElement) =>
    el.parentElement?.getBoundingClientRect().width ?? width ?? 0;

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      startX: e.clientX,
      startWidth: currentWidth(e.currentTarget),
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const next = Math.max(
      MIN_RESIZE_WIDTH,
      Math.round(drag.startWidth + e.clientX - drag.startX),
    );
    onResize(columnId, next);
  };

  const handlePointerEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const step = e.key === 'ArrowRight' ? KEYBOARD_RESIZE_STEP : -KEYBOARD_RESIZE_STEP;
    onResize(
      columnId,
      Math.max(MIN_RESIZE_WIDTH, Math.round(currentWidth(e.currentTarget) + step)),
    );
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={width}
      tabIndex={0}
      className={classes.resizeHandle}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onKeyDown={handleKeyDown}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onReset?.(columnId);
      }}
    />
  );
}

/**
 * A configurable, styled data table with column visibility, sorting,
 * sticky columns, horizontal scrolling, and pagination.
 */
export function ConfigurableTable<T>({
  data,
  columns,
  getRowKey,
  renderCell,
  renderActions,
  sortConfig,
  onSort,
  loading = false,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  visibleColumnCount,
  emptyMessage = 'No items found',
  emptyStateContent,
  hasActiveFilters = false,
  onClearFilters,
  stickyFirstColumn = false,
  stickyActions = false,
  getColumnMinWidth,
  getRowStyle,
  onRowClick,
  renderLeadingHeader,
  renderLeadingCell,
  leadingColumnWidth = 40,
  actionsColumnWidth = 50,
  entityLabel,
  viewportRef,
  resizableColumns = false,
  columnWidths,
  onColumnResize,
  onColumnResetWidth,
}: ConfigurableTableProps<T>) {
  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible),
    [columns],
  );

  const totalPages = Math.ceil(totalCount / pageSize);
  const hasData = data.length > 0;

  // Initial load with no data yet — show a full centered spinner
  if (loading && !hasData) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const renderColumnHeader = (column: ColumnConfig, index: number) => {
    const isFirst = index === 0;
    const isSticky = stickyFirstColumn && isFirst;
    const canResize = resizableColumns && !!onColumnResize;
    // MED-1367: a resizable header that is not sticky needs its own
    // positioning context for the handle. A sticky header has one already.
    const headerClass =
      [
        isSticky ? classes.stickyColumnHeader : undefined,
        canResize && !isSticky ? classes.resizableHeader : undefined,
        canResize ? classes.hasResizeHandle : undefined,
      ]
        .filter(Boolean)
        .join(' ') || undefined;
    // MED-1367: a set width pins the column (min and max), so content that
    // truncates at that width cannot grow it.
    const resizedWidth = canResize ? columnWidths?.[column.id] : undefined;
    const widthStyle: CSSProperties =
      resizedWidth !== undefined
        ? { width: resizedWidth, minWidth: resizedWidth, maxWidth: resizedWidth }
        : { minWidth: getColumnMinWidth?.(column.id) };
    const resizeHandle =
      canResize && onColumnResize ? (
        <ColumnResizeHandle
          columnId={column.id}
          ariaLabel={`Resize ${column.label} column`}
          width={resizedWidth}
          onResize={onColumnResize}
          onReset={onColumnResetWidth}
        />
      ) : null;

    if (column.sortable) {
      return (
        <Table.Th
          key={column.id}
          className={headerClass}
          style={widthStyle}
        >
          <SortableTableHeader
            label={column.label}
            sortKey={column.id}
            currentSort={sortConfig}
            onSort={onSort}
          />
          {resizeHandle}
        </Table.Th>
      );
    }
    return (
      <Table.Th
        key={column.id}
        className={headerClass}
        style={widthStyle}
      >
        {column.label}
        {resizeHandle}
      </Table.Th>
    );
  };

  const totalColSpan =
    visibleColumnCount +
    (renderActions ? 1 : 0) +
    (renderLeadingCell ? 1 : 0);

  const renderEmptyState = () => {
    if (emptyStateContent) {
      return (
        <Table.Tr>
          <Table.Td colSpan={totalColSpan}>
            {emptyStateContent}
          </Table.Td>
        </Table.Tr>
      );
    }
    return (
      <Table.Tr>
        <Table.Td colSpan={totalColSpan}>
          <Stack align="center" gap="xs" py="xl">
            <Text ta="center" c="dimmed">
              {emptyMessage}
            </Text>
            {hasActiveFilters && onClearFilters && (
              <Button variant="subtle" size="xs" onClick={onClearFilters}>
                Clear filters
              </Button>
            )}
          </Stack>
        </Table.Td>
      </Table.Tr>
    );
  };

  return (
    <Card
      withBorder
      p={0}
      radius={8}
      bg="white"
      style={{ boxShadow: 'none', position: 'relative' }}
      className={classes.tableCard}
    >
      <LoadingOverlay visible={loading} overlayProps={{ blur: 1 }} />
      <ScrollArea
        scrollbarSize={8}
        type="auto"
        offsetScrollbars={false}
        viewportRef={viewportRef}
      >
        <Table highlightOnHover className={classes.table}>
          <Table.Thead className={classes.tableHeader}>
            <Table.Tr>
              {renderLeadingHeader && (
                <Table.Th w={leadingColumnWidth}>
                  {renderLeadingHeader()}
                </Table.Th>
              )}
              {visibleColumns.map((col, i) => renderColumnHeader(col, i))}
              {renderActions && (
                <Table.Th
                  w={actionsColumnWidth}
                  className={stickyActions ? classes.actionsHeader : undefined}
                />
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.map((item) => {
              const rowStyle = getRowStyle?.(item);
              return (
                <Table.Tr
                  key={getRowKey(item)}
                  onClick={onRowClick ? () => onRowClick(item) : undefined}
                  className={`${classes.tableRow}${onRowClick ? ` ${classes.clickableRow}` : ''}`}
                  style={rowStyle}
                >
                  {renderLeadingCell && (
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      {renderLeadingCell(item)}
                    </Table.Td>
                  )}
                  {visibleColumns.map((col, index) => (
                    <Table.Td
                      key={col.id}
                      className={
                        stickyFirstColumn && index === 0
                          ? classes.stickyColumn
                          : undefined
                      }
                    >
                      {renderCell(item, col)}
                    </Table.Td>
                  ))}
                  {renderActions && (
                    <Table.Td
                      onClick={(e) => e.stopPropagation()}
                      className={
                        stickyActions ? classes.actionsCell : undefined
                      }
                    >
                      {renderActions(item)}
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
            {data.length === 0 && renderEmptyState()}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      {totalCount > 0 && (
        <Box className={classes.paginationWrapper}>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={totalCount}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            entityLabel={entityLabel}
          />
        </Box>
      )}
    </Card>
  );
}

export default ConfigurableTable;
